import { Injectable } from "@angular/core";
import { CatalogDto } from "../../models/generic/catalog.dto";
import { JSONHelper } from "./json.helper";
import * as moment from "moment";

@Injectable({
    providedIn: 'root'
})
export class StringUtilsHelper {

    /**
     * Función para agregar 2 digitos a un número en el siguiente formato '00'.
     * @param str Valor para asignar el digito faltante
     * @returns Regresa el valor str a dos digitos '00'
     */
    static twoDigits(str: string){
        if (str.length === 1) {
            str = '0' + str;
        }
        return str;
    }
    /**
     * Función para agregar 3 digitos a un número en el siguiente formato '000'.
     * @param str Valor para asignar el digito faltante
     * @returns Regresa el valor str a tres digitos '000'
     */
    static threeDigits(str: string){
        if (str.length === 1) {
            str = '00' + str;
        } else if (str.length === 2) {
            str = '0' + str;
        }
        return str;
    }

    static toUpper(data: string){
        return data.toUpperCase();
    }

    /**
     * Funcion para eliminar acentos o conocidos tambien como signos o simbolos diacríticos excepto a la ñ.
     * @param text Texto a normalizar sin acentos
     * @returns Retorna el texto sin acentos
     */
    static normalizeTextNFD(text) {
        return text.normalize('NFD').replace(/([^n\u0300-\u036f]|n(?!\u0303(?![\u0300-\u036f])))[\u0300-\u036f]+/gi,"$1").normalize();
    }

    static getYearCurp(curp: string){
        const yearNow = Number(moment().format("YY"));
        const yearShort: number = Number(curp.substring(4, 6));
        let yearCurp: number = 0;
        if(yearShort > yearNow){
            yearCurp = yearShort + 1900;
        } else {
            yearCurp = yearShort + 2000;
        }
        return yearCurp;
    }

    /**
     * Funcion que crea el nombre completo en una cadena despues de recibir los datos por separado.
     * @param names Recibe el nombre completo por separado, de preferencia en el orden de nombre(s) y apellidos
     * @returns Retorna el nombre completo en una sola cadena
     */
    static createFullName(...names : string[]) : string {
        return names.join(" ");
    }

    /**
     * Función para validar elementos de un objeto, para ello se recibe una lista de sus respectivos nombres de los elementos
     * para extraer la información, posteriormente regresa el resultado.
     * @param data Objeto de donde se validará información.
     * @param keys Lista de nombres de elementos dentro del data u objeto de datos fuente para hacer su respectiva validación
     * @returns Retorna True cuando todos los valores pasaron la validación
     */
    static notNullOrEmptyObj(data: any, ...keys : string[]){
        let validates: boolean[] = [];
        if (!data) {
            return true;
        } else {
            keys.forEach(key => {
                validates.push(data[key] === undefined || data[key] === null || data[key] === '');
            });
            let value = validates.filter(f => f === false);
            return (value.length === 0);
        }
    }

    /**
     * Función para validar un elemento si esta en null, undefined, o cadena vacía.
     * @param data Elemento a validar
     * @returns Retorna True si el objeto contiene datos, caso contrario retorna False.
     */
    static notNullOrUndefinedOrEmpty(data){
        if(data === undefined || data === null || data === ''){
            return false;
        }
        return true;
    }

    /**
     * Función para validar elementos de un objeto, recibiendo una lista de nombres de elementos.
     * @param data Objeto del cual se extrae la información para su validación.
     * @param keys Lista de nombres que se encuentran dentro del data o datos fuente para validar.
     * @returns Regresa False cuando todos los valores no tienen datos.
     */
    static notNullOrEmptyObjWithAnd(data: any, ...keys : string[]){
        let validates: boolean[] = [];
        if (!data) {
            return true;
        } else {
            keys.forEach(key => {
                validates.push(data[key] === undefined || data[key] === null || data[key] === '');
            });
            let value = validates.filter(f => f === false);
            return (value.length === keys.length);
        }
    }

    /**
     * Función para quitar los espacios en blanco de una cadena
     * @param data Cadena a la que se le eliminará los espacios en blanco
     * @returns Regresa la cadena sin espacios de más de la cadena
     */
    static cleanString(data: string){
        if (data) {
            return data.trim();
        }
        return "";
    }

    /**
     * Funcion para extrar el numero de años, meses o días para saber la edad a partir de la fecha de nacimiento.
     * @param birthDate Fecha de nacimiento
     * @returns Retorna el numero total de la edad
     */
    static getAgeNumber(birthDate: Date){
        let age: number = 0;
        let timeType: TimeType;

        const timeDiff = moment.duration(moment().diff(moment(birthDate)));
        const daysTotal = timeDiff.asMilliseconds() / (1000 * 3600 * 24);
        if (daysTotal >= 365) {
            age = Math.floor(daysTotal/365);
            timeType = TimeType.years;
        } else if(daysTotal > 30) {
            age = Math.floor(daysTotal/30);
            timeType = TimeType.months;
        } else {
            age = Math.floor(daysTotal);
            timeType = TimeType.days;
        }
        return {age: age, type: timeType};
    }

    /**
     * * Funcion para extrar el numero de años, meses o días para saber la edad a partir de la fecha de nacimiento (Date).
     * @param birthDate (Date)
     * @returns array {years, months, days}
     */
    static getAgeExactNumber(birthDate: Date){
        const today = moment();
        const birth = moment(birthDate);

        let ys = today.diff(birth, 'years');
        let ms = today.subtract(ys, 'years').diff(birth, 'months');
        let ds = today.subtract(ms, 'months').diff(birth, 'days');
        let hs = today.subtract(ds, 'days').diff(birth, 'hours');
        let ns = today.subtract(hs, 'hours').diff(birth, 'minutes');

        return {
            years: ys
            , months: ms
            , days: ds
            , hours: hs
            , minutes : ns
        };
    }

    static getAgeExactNumberWithDate(birthDate: Date, currentDate: Date) {
        if (!birthDate || !currentDate) return { years: 0, months: 0, days: 0, hours: 0, minutes: 0 };

    const today = moment(currentDate); // usamos la fecha que le pases
    const birth = moment(birthDate);

    let ys = today.diff(birth, 'years');
    let ms = today.subtract(ys, 'years').diff(birth, 'months');
    let ds = today.subtract(ms, 'months').diff(birth, 'days');
    let hs = today.subtract(ds, 'days').diff(birth, 'hours');
    let ns = today.subtract(hs, 'hours').diff(birth, 'minutes');

    return {
        years: ys,
        months: ms,
        days: ds,
        hours: hs,
        minutes: ns
    };
}


    /**
     * Funcion para calcular la edad desde la fecha de nacimiento tipo cadena
     * @param birthdate Fecha de nacimiento tipo string
     * @returns Retorna el valor de la edad calculado a la fecha actual
     */
    static calculateAge(birthdate: string): number {
        if (!birthdate) return 0;

        const birthDateObj = new Date(birthdate); // Convertir la cadena en una fecha
        const today = new Date(); // Fecha actual

        let age = today.getFullYear() - birthDateObj.getFullYear(); // Diferencia de años
        const monthDiff = today.getMonth() - birthDateObj.getMonth(); // Diferencia de meses
        const dayDiff = today.getDate() - birthDateObj.getDate(); // Diferencia de días

        // Si el mes actual es anterior al mes de nacimiento o estamos en el mes de nacimiento pero no se ha llegado al día, restamos 1 año
        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
            age--;
        }

        return age;
    }

    static calculateAgeFull(birthdate: string): { A: number, M: number, D: number } {
        if (!birthdate) return { A: 0, M: 0, D: 0 };

        const birthDateObj = new Date(birthdate);
        const today = new Date();

        let A = today.getFullYear() - birthDateObj.getFullYear();
        let M = today.getMonth() - birthDateObj.getMonth();
        let D = today.getDate() - birthDateObj.getDate();

        // Ajustar si el día actual es menor al día de nacimiento
        if (D < 0) {
            M--;
            const previousMonth = new Date(today.getFullYear(), today.getMonth(), 0);
            D += previousMonth.getDate();
        }

        // Ajustar si el mes actual es menor al mes de nacimiento
        if (M < 0) {
            A--;
            M += 12;
        }

        return { A, M, D };
    }

    static getMaxDate(date: Date, add: number, type: DateTimeType){
        let newDate = moment(date).add(add, type).format();
        return new Date(newDate);
    }

    static getMinDate(date: Date, subtract: number, type: DateTimeType){
        let newDate = moment(date).subtract(subtract, type).format();
        return new Date(newDate);
    }

    static formatParamGet(filter: string){
        return "?".concat(filter).substring(0, filter.length);
    }

    /**
     * Función para obtener un registro de una lista a partir de su id o identificador
     * @param list Lista de donde se extraerá el elemento
     * @param key Nombre del elemento que fungirá como filtro
     * @param id Valor del elemento para el filtro que es el id principal o identificador
     * @returns Regresa el elemento resultante de la busqueda por Id, en caso contrario solo retorna un valor vacio
     */
    static findByIdForList<T>(list: any, key: string, id: number){
        let obj: T = list.find(x => x[key] === id);
        if (obj) {
            return obj;
        }
        return {} as T;
    }

    /**
     * Función para obtener un registro de una lista a partir de un elemento de busqueda
     * @param list Lista de donde se extraerá el elemento
     * @param key Nombre del elemento que fungirá como filtro
     * @param data Nombre del elemento para hacer la busqueda en la lista
     * @returns Regresa el elemento resultante de la busqueda por 'nombre_elemento', en caso contrario solo retorna un valor vacio
     */
    static findByDataForList<T>(list: any, key: string, data: any){
        let obj: T = list.find(x => x[key] === data);
        if (obj) {
            return obj;
        }
        return undefined;
    }

    /**
     * Funcion para obtener una lista filtrada por el id o identificador.
     * @param list Lista de donde se extraerá el elemento
     * @param key Nombre del elemento que fungirá como filtro
     * @param id Valor del elemento para el filtro que es el id principal o identificador
     * @returns Regresa una lista filtrada resultante de la busqueda por Id, en caso contrario solo retorna un valor vacio
     */
    static filterByIdForList<T>(list: any, key: string, id: number){
        let obj: T[] = list.filter(x => x[key] === id);
        if (obj) {
            return obj;
        }
        return [];
    }

    /**
     * Funcion para obtener una lista filtrada por el id o identificador.
     * @param list Lista de donde se extraerá el elemento
     * @param key Nombre del elemento que fungirá como filtro
     * @param data Valor del elemento para realizar el filtro
     * @returns Regresa una lista filtrada resultante de la busqueda por Id, en caso contrario solo retorna un valor vacio
     */
    static filterByDataForList<T>(list: any, key: string, data: any){
        let obj: T[] = list.filter(x => x[key] === data);
        if (obj) {
            return obj;
        }
        return [];
    }

    /**
     * Función para crear listas genéricas para catalogo usando CatalogDto, usando sus elementos: 'catalogId' y 'catalogValue'
     * @param list Lista original de donde se extraerá la información para crear el genérico.
     * @param keyId Nombre del elemento de la lista original que hara referencia a catalogId para quedar como Id
     * @param keyName Nombre del elemento de la lista original que hara referencia a catalogValue para quedar como el valor del elemento
     * @returns Regresa una lista nueva generica creada con CatalogDto
     */
    static createCatalogGeneric(list: any, keyId: string, keyName: string){
        let newList: CatalogDto[] = [];
        list.forEach(element => {
            newList.push({
                catalogId: JSONHelper.getDataByKey(element, keyId),
                catalogValue: JSONHelper.getDataByKey(element, keyName),
            });
        });
        return newList;
    }

    /**
     * Función que verifica si en la lista solo tiene un registro y por lo tanto lo extrae.
     * @param list Lista a verificar
     * @returns Regresa solo el registro en caso de tener solo 1.
     */
    static getOnlyOneOfList(list: any[]){
        let obj: any;
        if (list.length === 1) {
            obj = list[0];
        }
        return obj;
    }

    static contains(list: any[], data: any){
        return list.includes(data);
    }

    static distinctList<T>(list: T[], idStr: string){
        list.sort((a, b) => a[idStr] - b[idStr]);
        const result = Array.from(new Set(list.map(s => s[idStr])))
            .map(id => {
                let obj = StringUtilsHelper.findByIdForList<T>(list, idStr, id);
                return obj
            });
        return result;
    }

}

/**
 * Tipo de tiempo, dias, meses o años, esto para definir algunos numeros de acuerdo a las fechas.
 */
export enum TimeType {
    days = 0,
    months = 1,
    years = 2
}

export enum TimeTypeStr {
    '0a' = 'days',
    '1a' = 'months',
    '2a' = 'years'
}

/**
 * Tipos de formato para fecha en el uso de Moment.
 */
export enum DateTimeType {
    /**
     * Hours
     */
    h = "h",
    /**
     * Minutes
     */
    m = "m",
    /**
     * Days
     */
    d = "d",
    /**
     * Weeks
     */
    w = "w",
    /**
     * Months
     */
    M = "M",
    /**
     * Years
     */
    y = "y",
}
